import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getUpiAppIndex } from '../services/dataService';
import { UpiAppIndex } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { useCountry } from '../context/CountryContext';

const UpiAppCard: React.FC<{ app: UpiAppIndex }> = ({ app }) => (
    <Link to={`/upi-charges/${app.slug}`} className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 ease-in-out transform hover:-translate-y-1 border border-gray-200">
        <div>
            <h3 className="font-semibold text-gray-800">{app.name}</h3>
        </div>
        <svg className="w-5 h-5 text-gray-400 ml-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    </Link>
);

const UpiChargesPage: React.FC = () => {
    const { country } = useCountry();
    const [apps, setApps] = useState<UpiAppIndex[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const pageDetails = useMemo(() => {
        if (country === 'IN') {
            return {
                title: 'UPI App Charges',
                description: 'Compare charges and fees for popular UPI apps in India.',
                searchPlaceholder: 'Search for a UPI app...',
                noAppsFoundMessage: 'Your search for "{searchTerm}" did not match any UPI apps in India.'
            };
        }
        if (country === 'US') {
            return {
                title: 'Digital Payment Apps',
                description: 'Compare charges for popular peer-to-peer payment apps in the US.',
                searchPlaceholder: 'Search for a payment app (e.g., Zelle, Venmo)...',
                noAppsFoundMessage: 'Your search for "{searchTerm}" did not match any payment apps in the US.'
            };
        }
        return {
            title: 'Payment Apps',
            description: 'Find charges and fees for digital payment apps.',
            searchPlaceholder: 'Search for a payment app...',
            noAppsFoundMessage: 'No payment apps found for your selected region.'
        };
    }, [country]);

    useEffect(() => {
        getUpiAppIndex(country).then(data => {
            setApps(data);
            setLoading(false);
        });
    }, [country]);

    const filteredApps = useMemo(() => {
        return apps.filter(app =>
            app.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [apps, searchTerm]);

    return (
        <div className="bg-gray-50 py-12">
            <div className="container mx-auto px-6 max-w-4xl">
                <header className="text-center mb-10">
                    <h1 className="text-4xl font-extrabold font-poppins tracking-tight text-gray-900 sm:text-5xl">{pageDetails.title}</h1>
                    <p className="mt-4 text-xl text-gray-600">{pageDetails.description}</p>
                </header>
                
                <div className="relative mb-8">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>
                    </div>
                    <input
                        type="search"
                        className="block w-full py-3 pl-12 pr-4 text-gray-900 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:ring-[#246BFD] focus:border-[#246BFD] text-base"
                        placeholder={pageDetails.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                {loading ? <LoadingSpinner /> : (
                    apps.length > 0 ? (
                        filteredApps.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {filteredApps.map(app => (
                                    <UpiAppCard key={app.id} app={app} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-10 px-6 bg-white rounded-lg shadow-sm border">
                                <h2 className="text-xl font-medium text-gray-800">No Apps Found</h2>
                                <p className="text-gray-500 mt-2">{pageDetails.noAppsFoundMessage.replace('{searchTerm}', searchTerm)}</p>
                            </div>
                        )
                    ) : (
                        <div className="text-center py-10 px-6 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                            <h2 className="text-xl font-medium text-blue-800 font-poppins">No Payment Apps Listed</h2>
                            <p className="text-blue-700 mt-2 max-w-2xl mx-auto">
                                {country === 'IN' 
                                    ? "No UPI apps are currently listed for India in our database." 
                                    : "There are no digital payment apps listed for your selected region in our database yet."}
                            </p>
                            {country !== 'IN' && 
                                <p className="text-blue-600 text-sm mt-4 max-w-2xl mx-auto">
                                    Note: UPI (Unified Payments Interface) is an instant payment system specific to India.
                                </p>
                            }
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default UpiChargesPage;
